import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getListFilesQueryKey,
  getListVectorStoresQueryKey,
} from "../api/generated/generated";
import type { FileResponse } from "../api/generated";
import { auth } from "../lib/firebase";

const API_URL = import.meta.env.VITE_API_URL || "";

/**
 * Direct file upload to the backend (multipart form data).
 * The backend handles GCS upload and file record creation in one step.
 */
export function useUploadFile(storeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File): Promise<FileResponse> => {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      const token = await currentUser.getIdToken();

      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${API_URL}/api/v1/vector-stores/${storeId}/upload`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Upload failed: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getListFilesQueryKey(storeId),
      });
      queryClient.invalidateQueries({
        queryKey: getListVectorStoresQueryKey(),
      });
    },
  });
}
